'use client';

import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect, useLayoutEffect, useRef } from 'react';
import { JS_VIEW_SCREEN_EVENT_NAME } from '@/common';
import { usePosthog } from '@/hooks/use-posthog';
import {
  getWebsiteTrackingScreen,
  rememberProfileViewSourceScreen,
} from '@/services/opportunity-tracking/source-screen';

type TrackedLocation = {
  pathname: string;
  search: string;
};

function isProfilePath(pathname: string): boolean {
  return pathname === '/ho-so' || pathname.startsWith('/ho-so/');
}

function getInitialSourceScreen(): string | undefined {
  try {
    if (!document.referrer) return undefined;

    const referrerUrl = new URL(document.referrer);

    if (referrerUrl.origin !== window.location.origin) return undefined;

    return getWebsiteTrackingScreen(
      referrerUrl.pathname,
      referrerUrl.searchParams
    );
  } catch {
    return undefined;
  }
}

export function PageVisitTracker() {
  const pathname = usePathname();
  const search = useSearchParams().toString();
  const { track } = usePosthog();

  const trackRef = useRef(track);
  const previousLocationRef = useRef<TrackedLocation | null>(null);
  const pageViewContextRef = useRef<{
    search: string;
    sourceScreen?: string;
  }>({ search: '' });

  useEffect(() => {
    trackRef.current = track;
  }, [track]);

  useLayoutEffect(() => {
    if (!pathname) return;

    const previousLocation = previousLocationRef.current;

    if (previousLocation?.pathname !== pathname) {
      pageViewContextRef.current = {
        search,
        sourceScreen: previousLocation
          ? getWebsiteTrackingScreen(
              previousLocation.pathname,
              new URLSearchParams(previousLocation.search)
            )
          : getInitialSourceScreen(),
      };

      if (
        previousLocation &&
        isProfilePath(pathname) &&
        !isProfilePath(previousLocation.pathname)
      ) {
        rememberProfileViewSourceScreen(
          previousLocation.pathname,
          new URLSearchParams(previousLocation.search)
        );
      }
    }

    previousLocationRef.current = {
      pathname,
      search,
    };
  }, [pathname, search]);

  useEffect(() => {
    if (!pathname) return;

    const { search: currentSearch, sourceScreen } = pageViewContextRef.current;
    const searchParams = new URLSearchParams(currentSearch);

    trackRef.current(
      JS_VIEW_SCREEN_EVENT_NAME,
      { source_screen: sourceScreen ?? '' },
      {
        transport: 'sendBeacon',
      }
    );

    const trackPageVisit = () => {
      trackRef.current(
        'js_visit_landing_page',
        {
          page_name: pathname === '/' ? 'home-page' : pathname,
          utm_source: searchParams.get('utm_source') ?? '',
          utm_campaign: searchParams.get('utm_campaign') ?? '',
          utm_medium: searchParams.get('utm_medium') ?? '',
          utm_content: searchParams.get('utm_content') ?? '',
        },
        {
          transport: 'sendBeacon',
        }
      );
    };

    if (typeof window.requestIdleCallback === 'function') {
      const idleId = window.requestIdleCallback(trackPageVisit, {
        timeout: 1500,
      });

      return () => {
        window.cancelIdleCallback(idleId);
      };
    }

    const timeoutId = globalThis.setTimeout(trackPageVisit, 500);

    return () => {
      globalThis.clearTimeout(timeoutId);
    };
  }, [pathname]);

  return null;
}
